"use client"

import { useTheme } from "@emotion/react"
import styled from "@emotion/styled"
import { Collapse, List, ListItemButton, ListItemText } from "@mui/material"
import Link from "next/link"
import { useState } from "react"
import { extractMenuProperties, getNonUmbracoUrl } from "utils"
import NewText from "../NewText/NewText"
import { Mobile } from "../common/bit/MediaQueries"
import { MenuArrowDown, MenuArrowUp } from "../icons"
import { sharedColors } from "../theme/colors"
import type { NavLinkVariant } from "./NavLink"

const StyledMenuItem = styled.div<{ selected: boolean; color?: string; variant?: NavLinkVariant }>`
  a {
    text-decoration: none;
    color: #000;
  }
  white-space: nowrap;
  p {
    color: ${(props) =>
      props.color ||
      (props.selected
        ? props.theme.colors.selectedSubNavTextColor
        : props.theme.colors.navTextColor || "inherit")};
    font-weight: ${(props) => (props.selected ? 600 : "normal")};
  }
  padding: 13px 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0;

    li {
      padding: 8px 16px;
    }
  }
  ${(props) =>
    props.variant === "port-mobile" &&
    `
      padding: 8px 0;
      p {
        color: ${
          props.theme.colors.port?.mobileMenuText || props.color || props.theme.colors.black
        };
        font-weight: ${props.selected ? 600 : 400};
      }
    `}
`

const StyledMobileButton = styled(ListItemButton)<{
  selected?: boolean
  color: string
  isLast?: boolean
  variant?: NavLinkVariant
}>`
  font-size: 18px;
  line-height: 24px;
  color: ${(props) => props.color};

  text-decoration: none;
  padding: 16px;
  p {
    color: ${(props) => props.color || (props.selected ? props.theme.colors.propBlue : "inherit")};
    font-weight: ${(props) => (props.selected ? 600 : "normal")};
  }
  path {
    fill: ${(props) => props.color || (props.selected ? props.theme.colors.propBlue : "#000000")};
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0px 24px;
    border-bottom: none;
  }
  ${(props) =>
    props.variant === "port-mobile" &&
    `
      padding: 12px 0;
      border-bottom: none;
      p {
        color: ${props.theme.colors.port?.mobileMenuText || props.color};
      }
      path {
        fill: ${props.theme.colors.port?.mobileMenuText || props.color};
      }
    `}
`

type Props = {
  data: any
  closeMenu: (() => void) | undefined
  color?: string
  title: string
  pathname: string
  variant?: NavLinkVariant
}

function SubNavLinks({
  data,
  color = sharedColors.black,
  title = "test",
  closeMenu,
  pathname,
  variant = "default",
}: Props) {
  const [isOpen, setOpen] = useState(false)
  const handleMobileClick = () => {
    setOpen(!isOpen)
  }
  const activeChildItem = data?.some((el: any) => {
    return decodeURI(pathname) + "/" === getNonUmbracoUrl(extractMenuProperties(el).url)
  })

  const theme = useTheme()
  const arrowColor =
    variant === "port-mobile" ? theme.colors.port?.mobileMenuText || "#0E2846" : "#1C1B1F"
  return (
    <Mobile>
      <StyledMobileButton
        color={color}
        id="basic-button"
        aria-controls={isOpen ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? "true" : undefined}
        onClick={handleMobileClick}
        selected={activeChildItem}
        variant={variant}
      >
        <ListItemText
          primary={
            <NewText
              component="p"
              desktopFontSize={16}
              desktopLineHeight={"20px"}
              mobileFontSize={16}
              mobileLineHeight={"14px"}
              mobileFontWeight={
                variant === "port-mobile" ? (activeChildItem ? 600 : 400) : undefined
              }
              color={
                variant === "port-mobile"
                  ? theme.colors.port?.mobileMenuText || sharedColors.black
                  : sharedColors.black
              }
            >
              {title}
            </NewText>
          }
        />
        {!isOpen ? <MenuArrowUp fill={arrowColor} /> : <MenuArrowDown fill={arrowColor} />}
      </StyledMobileButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit style={{ paddingRight: "16px" }}>
        <List
          component="div"
          disablePadding
          style={{
            borderRight:
              variant === "port-mobile" ? "none" : `solid 1px ${theme.colors.propBlue}52`,
          }}
        >
          {data.map((item: any, index: number) => {
            const { title, url } = item ? extractMenuProperties(item) : { title: "", url: "/" }
            const activeSubItem = decodeURI(pathname) + "/" === getNonUmbracoUrl(url)
            return (
              <>
                <StyledMenuItem
                  key={index.toString()}
                  selected={activeSubItem}
                  color={color}
                  variant={variant}
                >
                  <Link href={getNonUmbracoUrl(url)}>
                    <ListItemButton
                      onClick={() => closeMenu && closeMenu()}
                      sx={{ padding: 0, pl: variant === "port-mobile" ? 0 : 1.5 }}
                    >
                      <ListItemText
                        primary={
                          <NewText
                            component="p"
                            desktopFontSize={16}
                            desktopLineHeight={"14px"}
                            mobileFontSize={variant === "port-mobile" ? 16 : 14}
                            color={
                              variant === "port-mobile"
                                ? theme.colors.port?.mobileMenuText || sharedColors.black
                                : sharedColors.black
                            }
                          >
                            {title}
                          </NewText>
                        }
                      />
                    </ListItemButton>
                  </Link>
                </StyledMenuItem>
              </>
            )
          })}
        </List>
      </Collapse>
    </Mobile>
  )
}

export default SubNavLinks
